/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, View, ScrollView, FlatList, RefreshControl } from 'react-native';
;
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { ticketColors } from '../../shared/theme/ticketColors'
import { TicketDeskFooter } from '../../shared/ticket/TicketDeskFooter'
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { CustomerMovementHistoryModal } from '../components/CustomerMovementHistoryModal';
import { CustomerDetailModal } from '../components/CustomerDetailModal';
import { CustomerCard } from '../components/CustomerCard';
import { CustomerMovementModal } from '../components/CustomerMovementModal';
import { MercadoPagoQrPaymentModal } from '../../mercadopago/public-api';
import { CustomerMovementDetailModal } from '../components/CustomerMovementDetailModal';
import { CustomerEditorModal } from '../components/CustomerEditorModal';
import { CustomerReminderQueueModal } from '../components/CustomerReminderQueueModal';
import { CustomerFilterSheetContent, type CustomerFilterDraft } from '../components/CustomerFilterSheetContent';
import { CustomersScreenHeader } from '../components/CustomersScreenHeader';
import { FilterSheetButton } from '../../shared/filters/FilterSheetButton'
import { FilterSheetModal } from '../../shared/filters/FilterSheetModal';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { MobilePagination as StockMobilePagination } from '../../shared/ui/MobilePagination';
import { VIRTUALIZED_LIST_PROPS } from '../../shared/ui/virtualizedListProps';
import { resetCustomerTabOverlays, useCustomerOverlayStore } from '../store/useCustomerOverlayStore';
import { useResetModuleUiOnBlur } from '../../shell/navigation/useResetModuleUiOnBlur';
import { consumePreserveCustomerDetail } from './customerDetailPreserve';
import { useCustomersScreen } from './useCustomersScreen';
import { useCustomerMovementHistoryFlow } from './hooks/useCustomerMovementHistoryFlow';
import { useCustomerMovementFlow } from './hooks/useCustomerMovementFlow';
import { useCustomerEditorFlow } from './hooks/useCustomerEditorFlow';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities'
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider'
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { listActivePriceListsLocal } from '../../priceLists/public-api';
import { syncCatalogSnapshotFromRemote } from '../../catalog/infra/catalogSnapshotSync';
import { getCustomerAppointmentsPreview, type CustomerAppointmentPreview } from '../infra/customerRepository';
import { getCustomerAccountById } from '../infra/customerAccounts';
import type { CustomerMovementRow } from '../domain/customerTypes';
import { CsvImportExportModal } from '../../shared/csv/CsvImportExportModal';
import { createCustomerCsvAdapter } from '../infra/customerCsvAdapter';
import { customersScreenStyles as styles } from './customersScreen.styles';
import { useCustomerListPane } from './hooks/useCustomerListPane';
import { useCustomerDetailPane } from './hooks/useCustomerDetailPane';
import { useCustomerCollectPane } from './hooks/useCustomerCollectPane';

const NARROW_MODAL_WIDTH = 420;

export const CustomersScreen = () => {
  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const {
    layoutWidth,
    isWideCustomerLayout,
    canEdit,
    canEditCustomer,
    canViewDebt,
    canCollect,
    canAdjustDebt,
    canSetCreditLimit,
    canManageTasks,
    portalBaseUrl,
    paymentLinkAllowed,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    utilizationFilter,
    setUtilizationFilter,
    datePreset,
    setDatePreset,
    refreshing,
    handleRefresh,
    customers,
    customerPageIndex,
    customerPageSize,
    setCustomerPageSize,
    customerHasMore,
    customerLoading,
    customerError,
    customerResultTotalCount,
    loadCustomers,
    loadCustomerNextPage,
    loadCustomerPreviousPage,
    refreshCustomerTotalCount,
    customerListRef,
    customerWideScrollRef,
    formatMoney,
    bulkRemind,
    reminderQueue,
    closeReminderQueue,
    configurePortal,
    exportList,
    daySummary,
    daySummaryLoading,
    kpiSummary,
    kpiSummaryLoading,
  } = useCustomersScreen();
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const [customerCsvVisible, setCustomerCsvVisible] = useState(false);
  const [detailMovement, setDetailMovement] = useState<CustomerMovementRow | null>(null);
  const customerCsvAdapter = useMemo(() => createCustomerCsvAdapter(), []);

  const narrowActions = layoutWidth < NARROW_MODAL_WIDTH;

  const historyFlow = useCustomerMovementHistoryFlow({ formatMoney });
  const movementFlow = useCustomerMovementFlow({
    canCollect,
    canAdjustDebt,
    refreshCustomerTotalCount,
    loadCustomers,
    openCustomerHistory: historyFlow.openCustomerHistory,
  });
  const editorFlow = useCustomerEditorFlow({
    canEdit: canEditCustomer || canSetCreditLimit,
    refreshCustomerTotalCount,
    loadCustomers,
  });

  // Fetch appointment previews (last visit + next appointment) for the current page of customers.
  const [appointmentPreviews, setAppointmentPreviews] = useState<Map<string, CustomerAppointmentPreview>>(new Map());
  useEffect(() => {
    if (customers.length === 0) {
      setAppointmentPreviews(new Map());
      return;
    }
    let cancelled = false;
    const ids = customers.map((c) => c.id);
    void getCustomerAppointmentsPreview(ids).then((map) => {
      if (!cancelled) setAppointmentPreviews(map);
    });
    return () => { cancelled = true; };
  }, [customers]);

  const hasPriceListsCapability = useHasBusinessCapability('price_lists');
  const hasCreditFiadoCapability = useHasBusinessCapability('customer_credit_fiado');
  const { flags } = useGlobalFeatureFlags();
  const showPriceListField =
    hasPriceListsCapability && isGlobalFeatureOn(flags, 'price_lists_integrations');
  const [priceListOptions, setPriceListOptions] = useState<Array<{ value: string; label: string }>>([]);

  useEffect(() => {
    if (!editorFlow.customerEditorVisible || !showPriceListField) return;
    let cancelled = false;
    void (async () => {
      try {
        await syncCatalogSnapshotFromRemote({ force: true }).catch(() => undefined);
        const lists = await listActivePriceListsLocal();
        if (!cancelled) {
          setPriceListOptions(lists.map((list) => ({ value: list.id, label: list.name })));
        }
      } catch {
        if (!cancelled) setPriceListOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editorFlow.customerEditorVisible, showPriceListField]);

  const detailPane = useCustomerDetailPane({
    canEditCustomer: canEdit,
    canSetCreditLimit,
    canCollect,
    canAdjustDebt,
    canManageTasks,
    portalBaseUrl,
    paymentLinkAllowed,
    formatMoney: canViewDebt ? formatMoney : () => '•••',
    refreshCustomerTotalCount,
    loadCustomers,
    movementFlow,
    historyFlow,
    editorFlow,
  });

  const hasActiveCustomerListFilters = useMemo(
    () =>
      statusFilter !== 'all' ||
      utilizationFilter !== 'all' ||
      datePreset !== 'all' ||
      searchQuery.trim().length > 0,
    [statusFilter, utilizationFilter, datePreset, searchQuery]
  );

  const clearCustomerListFilters = useCallback(() => {
    setStatusFilter('all');
    setUtilizationFilter('all');
    setDatePreset('all');
    setSearchQuery('');
  }, [setStatusFilter, setUtilizationFilter, setDatePreset, setSearchQuery]);

  const listPane = useCustomerListPane({
    canEdit,
    customers,
    customerPageIndex,
    customerPageSize,
    setCustomerPageSize,
    customerHasMore,
    customerLoading,
    customerResultTotalCount,
    loadCustomerPreviousPage,
    loadCustomerNextPage,
    onAddCustomer: editorFlow.openNewCustomerEditor,
    customerError,
    onRetryLoad: () => void loadCustomers(),
    hasActiveFilters: hasActiveCustomerListFilters,
    onClearFilters: clearCustomerListFilters,
  });

  useCustomerCollectPane({
    canEdit: canCollect,
    openMovementModal: movementFlow.openMovementModal,
  });

  const pendingOpenNewCustomer = useCustomerOverlayStore((s) => s.pendingOpenNewCustomer);
  useEffect(() => {
    if (!pendingOpenNewCustomer) return;
    if (!useCustomerOverlayStore.getState().consumeOpenNewCustomerRequest()) return;
    editorFlow.openNewCustomerEditor();
  }, [pendingOpenNewCustomer, editorFlow.openNewCustomerEditor]);

  const pendingOpenCustomerDetailId = useCustomerOverlayStore(
    (s) => s.pendingOpenCustomerDetailId,
  );
  useEffect(() => {
    if (!pendingOpenCustomerDetailId) return;
    const customerId = useCustomerOverlayStore.getState().consumeOpenCustomerDetailRequest();
    if (!customerId) return;
    void getCustomerAccountById(customerId)
      .then((row) => {
        if (row) detailPane.openCustomerDetail(row);
      })
      .catch((err) => console.warn('[CustomersScreen] Error opening customer detail', err));
  }, [pendingOpenCustomerDetailId, detailPane.openCustomerDetail]);

  const renderCustomer = useCallback(
    ({ item }: { item: (typeof customers)[number] }) => (
      <CustomerCard
        item={item}
        onDetail={detailPane.openCustomerDetail}
        canViewDebt={canViewDebt}
        appointmentPreview={appointmentPreviews.get(item.id) ?? null}
      />
    ),
    [detailPane.openCustomerDetail, canViewDebt, appointmentPreviews]
  );

  const customerListEmpty = useCallback(
    () => <ModuleEmptyState {...listPane.listEmptyProps} />,
    [listPane.listEmptyProps]
  );

  const customerListSeparator = useCallback(() => null, []);

  const activeCustomerFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== 'all') count++;
    if (utilizationFilter !== 'all') count++;
    if (datePreset !== 'all') count++;
    return count;
  }, [statusFilter, utilizationFilter, datePreset]);

  const [customerFilterSheetOpen, setCustomerFilterSheetOpen] = useState(false);
  const [customerFilterDraft, setCustomerFilterDraft] = useState<CustomerFilterDraft | null>(null);

  const openCustomerFilterSheet = useCallback(() => {
    setCustomerFilterDraft({
      statusFilter,
      sortBy,
      utilizationFilter,
      datePreset,
    });
    setCustomerFilterSheetOpen(true);
  }, [statusFilter, sortBy, utilizationFilter, datePreset]);

  const applyCustomerFilterDraft = useCallback(() => {
    if (!customerFilterDraft) return;
    if (customerFilterDraft.statusFilter !== statusFilter) setStatusFilter(customerFilterDraft.statusFilter);
    if (customerFilterDraft.sortBy !== sortBy) setSortBy(customerFilterDraft.sortBy);
    if (customerFilterDraft.utilizationFilter !== utilizationFilter) setUtilizationFilter(customerFilterDraft.utilizationFilter);
    if (customerFilterDraft.datePreset !== datePreset) setDatePreset(customerFilterDraft.datePreset);
    setCustomerFilterSheetOpen(false);
  }, [customerFilterDraft, statusFilter, sortBy, utilizationFilter, datePreset, setStatusFilter, setSortBy, setUtilizationFilter, setDatePreset]);

  const clearCustomerFilterDraft = useCallback(() => {
    setCustomerFilterDraft({
      statusFilter: 'all',
      sortBy: 'updated_desc',
      utilizationFilter: 'all',
      datePreset: 'all',
    });
  }, []);

  const customerFilterTrigger = useMemo(
    () => (
      <FilterSheetButton
        activeCount={activeCustomerFilterCount}
        onOpen={openCustomerFilterSheet}
        variant={isWideCustomerLayout ? 'desk' : 'ticket'}
      />
    ),
    [activeCustomerFilterCount, openCustomerFilterSheet, isWideCustomerLayout]
  );

  useResetModuleUiOnBlur(
    useCallback(() => {
      if (consumePreserveCustomerDetail()) {
        return;
      }
      detailPane.blockFlow.closeBlockConfirm();
      detailPane.closeCustomerDetail();
      historyFlow.closeCustomerHistory();
      editorFlow.closeCustomerEditor();
      resetCustomerTabOverlays();
    }, [
      detailPane.blockFlow.closeBlockConfirm,
      detailPane.closeCustomerDetail,
      historyFlow.closeCustomerHistory,
      editorFlow.closeCustomerEditor,
    ])
  );

  const customerOverlays = (
    <>
      <CustomerMovementModal
        visible={movementFlow.movementModal}
        onClose={movementFlow.closeMovementModal}
        customer={movementFlow.selectedCustomer}
        movements={movementFlow.movements}
        loading={movementFlow.movementLoading}
        movementsHasMore={movementFlow.movementsHasMore}
        movementsLoadingMore={movementFlow.movementsLoadingMore}
        onLoadMore={movementFlow.loadMoreMovements}
        amount={movementFlow.amountInput}
        note={movementFlow.noteInput}
        setAmount={movementFlow.setAmountInput}
        setNote={movementFlow.setNoteInput}
        onCharge={() => void movementFlow.handleRegisterMovement('charge')}
        onPayment={() => void movementFlow.handleRegisterMovement('payment')}
        saving={movementFlow.savingMovement}
        canCollect={canCollect}
        canAdjustDebt={canAdjustDebt}
        previewMode
        onOpenFullHistory={movementFlow.openFullHistoryFromMovement}
        selectedSaleId={movementFlow.selectedSaleId}
        setSelectedSaleId={movementFlow.setSelectedSaleId}
        customerSalesOptions={movementFlow.customerSalesOptions}
        loadingCustomerSales={movementFlow.loadingCustomerSales}
        paymentMethod={movementFlow.paymentMethod}
        setPaymentMethod={movementFlow.setPaymentMethod}
        checkDrafts={movementFlow.checkDrafts}
        updateCheckDraft={movementFlow.updateCheckDraft}
        addCheckDraft={movementFlow.addCheckDraft}
        removeCheckDraft={movementFlow.removeCheckDraft}
        checksEnabled={movementFlow.hasChecksCapability}
        mercadopagoQrEnabled={movementFlow.mercadopagoQrAllowed}
        cashLocked={
          movementFlow.hasCashSessionsCapability && !movementFlow.activeCashSessionId
        }
        pendingAttachments={movementFlow.pendingAttachments}
        attachmentBusy={movementFlow.attachmentBusy}
        onAddAttachment={() => void movementFlow.addMovementAttachment()}
        onRemoveAttachment={movementFlow.removeMovementAttachment}
        onOpenMovementDetail={setDetailMovement}
        confirmOverlay={
          movementFlow.movementConfirmModalProps
            ? {
                ...movementFlow.movementConfirmModalProps,
                onClose: movementFlow.closeMovementConfirm,
                onConfirm: movementFlow.confirmMovementStep,
              }
            : null
        }
      />
      <MercadoPagoQrPaymentModal
        variant="customer_payment"
        visible={movementFlow.qrIntent != null}
        phase={movementFlow.qrSessionState.phase}
        loading={movementFlow.qrSessionState.loading}
        error={movementFlow.qrSessionState.error}
        qrData={movementFlow.qrSessionState.qrData}
        qrUrl={null}
        amount={movementFlow.qrIntent?.amount ?? movementFlow.qrSessionState.amount}
        saleRef={null}
        expiresAt={movementFlow.qrSessionState.expiresAt}
        countdownSeconds={movementFlow.qrSessionState.countdownSeconds}
        fiscalInvoiceLabel={null}
        autoCloseProgress={null}
        lastMpQrSaleId={null}
        onClose={movementFlow.closeQrSession}
        onRegenerate={movementFlow.regenerateQr}
        onCancelIntent={() => void movementFlow.cancelQrIntent()}
      />
      <CustomerMovementHistoryModal
        visible={historyFlow.customerHistoryVisible}
        customerName={historyFlow.customerHistoryCustomer?.name ?? ''}
        movements={historyFlow.customerHistoryRows}
        historyLoading={historyFlow.customerHistoryLoading}
        historyLoadingMore={historyFlow.customerHistoryLoadingMore}
        historyHasMore={historyFlow.customerHistoryHasMore}
        onLoadMore={historyFlow.loadMoreCustomerHistory}
        onClose={historyFlow.closeCustomerHistory}
        actorLabel={historyFlow.actorLabel}
        typeOptions={historyFlow.customerTypeOptions}
        typeFilter={historyFlow.customerHistoryTypeFilter}
        onTypeFilterChange={historyFlow.setCustomerHistoryTypeFilter}
        fromDate={historyFlow.customerHistoryFromDate}
        toDate={historyFlow.customerHistoryToDate}
        onFromDateChange={historyFlow.setCustomerHistoryFromDate}
        onToDateChange={historyFlow.setCustomerHistoryToDate}
        onApplyFilters={historyFlow.applyCustomerHistoryFilters}
        onExportCsv={historyFlow.exportCustomerHistoryCsv}
        formatMoney={formatMoney}
        onPressMovement={setDetailMovement}
      />
      <CustomerMovementDetailModal
        visible={detailMovement != null}
        movement={detailMovement}
        canEdit={canCollect}
        onClose={() => setDetailMovement(null)}
      />
      <CustomerReminderQueueModal
        visible={reminderQueue != null}
        customers={reminderQueue ?? []}
        paymentLinkAllowed={paymentLinkAllowed}
        portalBaseUrl={portalBaseUrl}
        onClose={closeReminderQueue}
      />
      <CsvImportExportModal
        visible={customerCsvVisible}
        onClose={() => setCustomerCsvVisible(false)}
        mode="customers"
        kicker="Clientes"
        canEdit={canEditCustomer}
        filteredExportLabel="Listado filtrado"
        onDownloadFilteredExport={() => void exportList()}
        onApplied={handleRefresh}
        adapter={customerCsvAdapter}
      />
      <CustomerEditorModal
        visible={editorFlow.customerEditorVisible}
        narrowActions={narrowActions}
        editingCustomer={editorFlow.editingCustomer}
        formName={editorFlow.formName}
        formPhone={editorFlow.formPhone}
        formCuit={editorFlow.formCuit}
        formLimit={editorFlow.formLimit}
        formEmail={editorFlow.formEmail}
        formAddress={editorFlow.formAddress}
        formTaxCategory={editorFlow.formTaxCategory}
        formNotes={editorFlow.formNotes}
        formPaymentTermsDays={editorFlow.formPaymentTermsDays}
        formPriceListId={editorFlow.formPriceListId}
        priceListOptions={priceListOptions}
        showPriceListField={showPriceListField}
        showCreditFields={hasCreditFiadoCapability}
        savingCustomerForm={editorFlow.savingCustomerForm}
        discardConfirmVisible={editorFlow.discardConfirmVisible}
        duplicateWarning={editorFlow.duplicateWarning}
        onFormNameChange={editorFlow.setFormName}
        onFormPhoneChange={editorFlow.setFormPhone}
        onFormCuitChange={editorFlow.setFormCuit}
        onFormLimitChange={editorFlow.setFormLimit}
        onFormEmailChange={editorFlow.setFormEmail}
        onFormAddressChange={editorFlow.setFormAddress}
        onFormTaxCategoryChange={editorFlow.setFormTaxCategory}
        onFormNotesChange={editorFlow.setFormNotes}
        onFormPaymentTermsDaysChange={editorFlow.setFormPaymentTermsDays}
        onFormPriceListIdChange={editorFlow.setFormPriceListId}
        onTryClose={editorFlow.tryCloseCustomerEditor}
        onCloseDiscardConfirm={() => editorFlow.setDiscardConfirmVisible(false)}
        onDiscard={editorFlow.closeCustomerEditor}
        onSave={() => void editorFlow.saveCustomerEditor()}
        onConfirmCreateAnyway={() => void editorFlow.confirmCreateAnyway()}
        onDismissDuplicateWarning={editorFlow.dismissDuplicateWarning}
        branchId={currentBranchId}
      />
    </>
  );

  const readOnlyBanner = !canEdit ? (
    <View style={posUi.readOnlyBanner}>
      <Text style={posUi.readOnlyText}>Solo lectura. Podés ver los clientes, no cambiarlos.</Text>
    </View>
  ) : null;

  const headerProps = {
    rows: customers,
    loading: customerLoading,
    search: searchQuery,
    onSearchChange: setSearchQuery,
    canEdit,
    onAddCustomer: editorFlow.openNewCustomerEditor,
    onBulkRemind: bulkRemind,
    onOpenCsv: () => setCustomerCsvVisible(true),
    onConfigurePortal: portalBaseUrl ? configurePortal : undefined,
    onFilterOverdue: () => setStatusFilter('overdue'),
    overdueSelected: statusFilter === 'overdue',
    formatMoney,
    filterTrigger: customerFilterTrigger,
    daySummary: canViewDebt ? daySummary : null,
    daySummaryLoading,
    kpiSummary: canViewDebt ? kpiSummary : null,
    kpiSummaryLoading,
  };

  return (
    <ShellContentFrame
      style={[styles.container, !isWideCustomerLayout && styles.containerCompact]}
    >
      {isWideCustomerLayout ? (
        <ScrollView
          style={styles.listFlex}
          contentContainerStyle={styles.wideScrollContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[ticketColors.gold]}
              tintColor={ticketColors.gold}
            />
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          ref={customerWideScrollRef}
        >
          <View style={styles.stack}>
            {readOnlyBanner}
            <CustomersScreenHeader {...headerProps} />
            {customers.length === 0 ? (
              <ModuleEmptyState {...listPane.listEmptyProps} />
            ) : (
              <PaperSheet style={styles.listSheet} testID="customers.list.sheet">
                {customers.map((customer) => (
                  <CustomerCard
                    key={customer.id}
                    item={customer}
                    onDetail={detailPane.openCustomerDetail}
                    canViewDebt={canViewDebt}
                    appointmentPreview={appointmentPreviews.get(customer.id) ?? null}
                  />
                ))}
                <TicketDeskFooter
                  pageIndex={listPane.paginationProps.catalogPageIndex}
                  pageSize={listPane.paginationProps.pageSize}
                  productCount={listPane.paginationProps.productCount}
                  totalCount={listPane.paginationProps.catalogTotalCount}
                  hasMore={listPane.paginationProps.hasMore}
                  loading={listPane.paginationProps.productsLoading}
                  onPreviousPage={listPane.paginationProps.onPreviousPage}
                  onNextPage={listPane.paginationProps.onNextPage}
                  embedded
                />
              </PaperSheet>
            )}
          </View>
        </ScrollView>
      ) : (
        <FlatList
          ref={customerListRef}
          style={styles.listFlex}
          data={customers}
          keyExtractor={listPane.customerKeyExtractor}
          contentContainerStyle={listPane.customerListContentStyle}
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
          showsVerticalScrollIndicator
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[ticketColors.gold]} tintColor={ticketColors.gold} />
          }
          ListHeaderComponent={
            <View style={styles.stack}>
              {readOnlyBanner}
              <CustomersScreenHeader {...headerProps} />
            </View>
          }
          ListFooterComponent={
            <StockMobilePagination
              pageIndex={customerPageIndex}
              pageSize={customerPageSize}
              totalCount={customerResultTotalCount}
              productCount={customers.length}
              loading={customerLoading}
              hasMore={customerHasMore}
              onPreviousPage={loadCustomerPreviousPage}
              onNextPage={loadCustomerNextPage}
            />
          }
          ListEmptyComponent={customerListEmpty}
          ItemSeparatorComponent={customerListSeparator}
          renderItem={renderCustomer}
          {...VIRTUALIZED_LIST_PROPS}
        />
      )}
      <CustomerDetailModal {...detailPane.detailModalProps} layoutWide={isWideCustomerLayout} />
      {customerOverlays}
      {movementFlow.SupervisorPinModal}

      <FilterSheetModal
        visible={customerFilterSheetOpen}
        onClose={() => setCustomerFilterSheetOpen(false)}
        onApply={applyCustomerFilterDraft}
        onClear={clearCustomerFilterDraft}
        appearance="ticket"
        kicker="Clientes"
        subtitle="Acotá la lista."
        hasActiveDraft={
          customerFilterDraft != null &&
          (customerFilterDraft.statusFilter !== 'all' ||
            customerFilterDraft.sortBy !== 'updated_desc' ||
            customerFilterDraft.utilizationFilter !== 'all' ||
            customerFilterDraft.datePreset !== 'all')
        }
      >
        {customerFilterDraft ? (
          <CustomerFilterSheetContent
            draft={customerFilterDraft}
            setDraft={(updater) => setCustomerFilterDraft((prev) => (prev ? updater(prev) : prev))}
          />
        ) : null}
      </FilterSheetModal>
    </ShellContentFrame>
  );
};
